import BiotechRoundedIcon from "@mui/icons-material/BiotechRounded";
import TopicIcon from "@mui/icons-material/Topic";
import {
  Box,
  CircularProgress,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  DataGridProps,
  GridColDef,
  GridColumnHeaderTitle,
} from "@mui/x-data-grid";
import dayjs from "dayjs";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import DisplayDate from "../../components/DisplayDate";
import ExamPacsAction from "../../components/Exams/ExamPacsAction/ExamPacsAction";
import { IExamsWithPacsValidation } from "../../components/Exams/ExamPacsAction/models";
import ExamReportAction from "../../components/Exams/ExamReportAction/ExamReportAction";
import ExamsRedirectFile from "../../components/Exams/ExamsRedirectFile/ExamsRedirectFile";
import BaseTable from "../../components/UI/Table/BaseTable";
import { useExtensionConfigs } from "../../hooks/useExtensionConfigs";
import useSiteApp from "../../hooks/useSiteApp";
import { useAppSelector } from "../../store/store";
import { IExams } from "../../models/exams";
import { Extension } from "../../models/intalled-extensions";
import ManagementLaboratorialExamsRowActions from "./ManagementLaboratorialExamsRowActions";

const EXAM_MANAGER_PROFILE_CODE = "11";

const translationPath = "page.exams.";

interface IManagementLaboratorialExamsTableProps {
  data: IExams[];
  individualId?: string;
  tableProps?: Omit<DataGridProps, "columns" | "rows" | "sx">;
  hasPixeonExtension: boolean;
  hasVueMotionExtension: boolean;
  hasExamPacsAction: boolean;
  loadingPacsValues: boolean;
  pacsColumnName: string | null;
  onEmptyReleaseDateSuccess: () => void;
}

const ManagementLaboratorialExamsTable: React.FC<
  IManagementLaboratorialExamsTableProps
> = ({
  data,
  tableProps,
  individualId,
  hasPixeonExtension,
  hasVueMotionExtension,
  hasExamPacsAction,
  loadingPacsValues,
  pacsColumnName,
  onEmptyReleaseDateSuccess,
}) => {
  const { t } = useTranslation();

  const {
    config: { sideMenu },
  } = useSiteApp();

  const { individual } = useAppSelector((state) => state.user);

  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down("lg"));

  const [cdnUrl, setCdnUrl] = useState<boolean>(false);
  const [checkPdfSerialOnNullFormat, setCheckPdfSerialOnNullFormat] =
    useState<boolean>(false);
  const { fetchExtensionConfig } = useExtensionConfigs();

  const fetchExtensions = useCallback(async () => {
    const configCdnUrl = await fetchExtensionConfig(Extension.EXAMS, "CDNURL");
    setCdnUrl(!!configCdnUrl && configCdnUrl.value !== ".");

    const configCheckPdfSerialOnNullFormat = await fetchExtensionConfig(
      Extension.EXAMS,
      "CheckPdfSerialOnNullFormat",
    );
    setCheckPdfSerialOnNullFormat(
      configCheckPdfSerialOnNullFormat?.value === "1",
    );
  }, [fetchExtensionConfig]);

  useEffect(() => {
    fetchExtensions();
  }, [fetchExtensions]);

  const fixMalformedYear = (dateStr: string): string => {
    if (!dateStr) return dateStr;
    const match = dateStr.match(/^(\d{4})(-.+)$/);
    if (!match) return dateStr;
    const year = parseInt(match[1], 10);
    if (year < 100) {
      const correctedYear = 2000 + (year % 100);
      return `${correctedYear}${match[2]}`;
    }
    return dateStr;
  };

  const hasExamManagerProfile = individual.profiles.some(
    (profile) => profile.code === EXAM_MANAGER_PROFILE_CODE,
  );

  const hasAnyExamWithEmptyReleaseDate = useMemo(() => {
    return data.some(
      (exam) =>
        exam.type !== "LABORATORIAL" &&
        !!exam.sequencialControl &&
        hasExamManagerProfile,
    );
  }, [data, hasExamManagerProfile]);

const columns: GridColDef<IExams>[] = useMemo(() => {
    const baseColumns: GridColDef<IExams>[] = [];

    if (hasAnyExamWithEmptyReleaseDate) {
      baseColumns.push({
        field: "actions",
        headerName: "",
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        width: 44,
        renderCell: (params) => (
          <ManagementLaboratorialExamsRowActions
            exam={params.row}
            onEmptyReleaseDateSuccess={onEmptyReleaseDateSuccess}
          />
        ),
      });
    }

    baseColumns.push(
      {
        field: "individualName",
        headerName: "Paciente",
        flex: 1,
        minWidth: 190,
      },
      {
        field: "type",
        headerName: `${t(translationPath + "type")}`,
        width: 92,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          const isLab = params.value === "LABORATORIAL";
          const title = isLab ? "Laboratorial" : "Nao laboratorial";

          return (
            <Tooltip title={title}>
              {isLab ? (
                <BiotechRoundedIcon color="primary" fontSize="small" />
              ) : (
                <TopicIcon color="action" fontSize="small" />
              )}
            </Tooltip>
          );
        },
      },
      {
        field: "medicalCertificateName",
        headerName: `${t(translationPath + "procedure")}`,
        flex: 1,
        minWidth: 260,
      },
      {
        field: "physicianName",
        headerName: `${t(translationPath + "doctor")}`,
        flex: 1,
      },
      {
        field: "establishmentName",
        headerName: `${t(translationPath + "unit")}`,
        flex: 1,
      },
      {
        field: "medicalCertificateDate",
        headerName: `${t(translationPath + "date")}`,
        flex: 1,
        maxWidth: 150,
        renderCell: (params) => (
          <DisplayDate typography={{ variant: "body2" }}>
            {dayjs(fixMalformedYear(params.value))}
          </DisplayDate>
        ),
      },
      {
        field: "sequencialImageControls",
        headerName: `${t(translationPath + "image")}`,
        flex: 1,
        maxWidth: 100,
        renderCell: (params) =>
          params.value.length === 0 ? (
            "-"
          ) : (
            <ExamsRedirectFile
              sequencialControl={params.row.sequencialControl}
              sequencialImageControls={params.row.sequencialImageControls}
            />
          ),
      },
      {
        field: "download",
        headerName: `${t(translationPath + "report")}`,
        flex: 1,
        maxWidth: 100,
        renderCell: (params) => (
          <ExamReportAction
            exam={params.row}
            cdnUrl={cdnUrl}
            checkPdfSerialOnNullFormat={checkPdfSerialOnNullFormat}
            individualId={individualId}
          />
        ),
      },
    );

    if (hasExamPacsAction || loadingPacsValues) {
      baseColumns.push({
        field: "pixeonPacs",
        flex: 1,
        maxWidth: 100,
        renderHeader: (params) => {
          if (loadingPacsValues) {
            return (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "4px",
                }}
              >
                <CircularProgress size={15} />
              </Box>
            );
          }

          return (
            <GridColumnHeaderTitle
              label={pacsColumnName || t(translationPath + "pacs")}
              columnWidth={params.colDef.computedWidth}
            />
          );
        },
        renderCell: ({ row }) =>
          loadingPacsValues ? (
            <CircularProgress size={15} />
          ) : (
            <ExamPacsAction
              exam={row as IExamsWithPacsValidation}
              hasPixeonExtension={hasPixeonExtension}
              hasVueMotionExtension={hasVueMotionExtension}
            />
          ),
      });
    }

    return baseColumns;
  }, [
    t,
    individualId,
    cdnUrl,
    checkPdfSerialOnNullFormat,
    hasPixeonExtension,
    hasVueMotionExtension,
    hasExamPacsAction,
    loadingPacsValues,
    pacsColumnName,
    onEmptyReleaseDateSuccess,
    data,
    hasExamManagerProfile,
  ]);

  return (
    <BaseTable<IExams>
      {...tableProps}
      columns={columns}
      rows={data}
      paginationMode="server"
      sx={{
        height: matches
          ? "calc(100vh - 340px)"
          : sideMenu
            ? "calc(100vh - 240px)"
            : "calc(100vh - 260px)",
      }}
    />
  );
};

export default memo(ManagementLaboratorialExamsTable);
